import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { keycloak, initKeycloakOnce, isKeycloakConfigValid } from './keycloak';
import {
  getRoles,
  hasRole as authHasRole,
  hasAnyRole as authHasAnyRole,
  type AuthUser,
} from './auth';
import { clearTokens, loadTokens, saveTokens } from './tokenStorage';

const LOGIN_CALLBACK_PARAMS = ['access_token', 'code=', 'state='];
const MIN_TOKEN_VALIDITY = 30;
const MICROSOFT_IDP_HINT = 'microsoft';

function loginWithMicrosoft() {
  if (!keycloak) return Promise.resolve();
  return keycloak.login({ idpHint: MICROSOFT_IDP_HINT });
}

function persistCurrentSession() {
  if (!keycloak?.token || !keycloak.refreshToken) return;
  saveTokens({
    token: keycloak.token,
    refreshToken: keycloak.refreshToken,
    idToken: keycloak.idToken,
  });
}

/** Refresh on expiry; if refresh fails, clear tokens and re-login via Microsoft. */
function handleTokenExpired() {
  if (!keycloak) return;
  keycloak
    .updateToken(MIN_TOKEN_VALIDITY)
    .then(() => {
      persistCurrentSession();
    })
    .catch(() => {
      clearTokens();
      loginWithMicrosoft();
    });
}

export type User = AuthUser;

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  error: string | null;
  isInitialized: boolean;
}

export interface AuthContextType extends AuthState {
  login: () => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function readUser(keycloakInstance: any): AuthUser {
  const tokenParsed = keycloakInstance.tokenParsed as Record<string, unknown> | undefined;
  const roles = getRoles(tokenParsed);
  return {
    id: (tokenParsed?.sub as string) || '',
    email: (tokenParsed?.email as string) || '',
    name: (tokenParsed?.name as string) || '',
    firstName: (tokenParsed?.given_name as string) || '',
    lastName: (tokenParsed?.family_name as string) || '',
    username: (tokenParsed?.preferred_username as string) || '',
    roles,
    isActive: true,
    lastLoginAt: new Date().toISOString(),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

function cleanLoginCallbackUrl() {
  const isCallback = LOGIN_CALLBACK_PARAMS.some(
    (p) => window.location.hash.includes(p) || window.location.search.includes(p)
  );
  if (isCallback) {
    window.history.replaceState(
      {},
      document.title,
      window.location.pathname + window.location.search
    );
  }
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null,
    isInitialized: false,
  });
  const didInit = useRef(false);

  const applyAuthenticated = useCallback((user: User) => {
    persistCurrentSession();
    setState({
      isAuthenticated: true,
      isLoading: false,
      user,
      error: null,
      isInitialized: true,
    });
  }, []);

  const setUnauthenticated = useCallback((error: string | null = null) => {
    clearTokens();
    setState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      error,
      isInitialized: true,
    });
  }, []);

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    if (!isKeycloakConfigValid || !keycloak) {
      setUnauthenticated('Keycloak configuration is missing. Set REACT_APP_KEYCLOAK_* and restart.');
      return;
    }

    const persisted = loadTokens();

    initKeycloakOnce(
      persisted
        ? {
            token: persisted.token,
            refreshToken: persisted.refreshToken,
            idToken: persisted.idToken,
          }
        : undefined
    )
      .then((authenticated) => {
        if (!authenticated) {
          setUnauthenticated();
          return;
        }

        cleanLoginCallbackUrl();
        keycloak.onTokenExpired = handleTokenExpired;
        applyAuthenticated(readUser(keycloak));
      })
      .catch((err: any) => {
        if (persisted) {
          setUnauthenticated();
          return;
        }

        let msg = 'Authentication initialization failed';
        const detail = err?.error || err?.message || '';
        if (String(detail).includes('3rd party')) msg = 'Third-party cookies are blocked.';
        else if (String(detail).includes('Timeout')) msg = 'Authentication server timeout.';
        else if (String(detail).includes('Network')) msg = 'Cannot connect to authentication server.';
        setUnauthenticated(msg);
      });
  }, [applyAuthenticated, setUnauthenticated]);

  const login = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      if (!keycloak) throw new Error('Keycloak not initialized');
      await loginWithMicrosoft();
    } catch (e) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: 'Login failed: ' + (e instanceof Error ? e.message : 'Unknown error'),
      }));
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      [
        'calibraone_sensor_types_cache',
        'calibraone_sensors_cache',
        'calibraone_api_sensor_types_cache',
        'calibraone_api_sensors_cache',
      ].forEach((k) => {
        try {
          sessionStorage.removeItem(k);
        } catch {
          // ignore
        }
      });
      clearTokens();
      if (keycloak) {
        await keycloak.logout({ redirectUri: window.location.origin });
      }
    } catch {
      setState((prev) => ({ ...prev, isLoading: false, error: 'Logout failed' }));
    }
  }, []);

  const checkRole = useCallback(
    (role: string) => {
      if (state.user?.roles?.includes(role)) return true;
      const tokenParsed = keycloak?.tokenParsed as Record<string, unknown> | undefined;
      return authHasRole(tokenParsed, role);
    },
    [state.user]
  );

  const checkAnyRole = useCallback(
    (roles: string[]) => {
      const userRoles = state.user?.roles;
      if (userRoles?.length && roles.some((r) => userRoles.includes(r))) return true;
      const tokenParsed = keycloak?.tokenParsed as Record<string, unknown> | undefined;
      return authHasAnyRole(tokenParsed, roles);
    },
    [state.user]
  );

  const clearError = useCallback(() => setState((prev) => ({ ...prev, error: null })), []);

  useEffect(() => {
    if (!keycloak) return;
    keycloak.onTokenExpired = handleTokenExpired;
    keycloak.onAuthSuccess = () => {
      if (!keycloak.authenticated) return;
      cleanLoginCallbackUrl();
      applyAuthenticated(readUser(keycloak));
    };
    keycloak.onAuthError = () =>
      setState((prev) => ({ ...prev, error: 'Authentication error occurred', isLoading: false }));
    keycloak.onAuthLogout = () =>
      setState((prev) => ({
        ...prev,
        isAuthenticated: false,
        user: null,
      }));
    return () => {
      keycloak.onTokenExpired = undefined;
      keycloak.onAuthSuccess = undefined;
      keycloak.onAuthError = undefined;
      keycloak.onAuthLogout = undefined;
    };
  }, [applyAuthenticated]);

  const contextValue: AuthContextType = {
    ...state,
    login,
    logout,
    hasRole: checkRole,
    hasAnyRole: checkAnyRole,
    clearError,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

export { AuthContext };
